import { useEffect, useState } from 'react';
import type { DriveDocuments, DriveFile, DriveFolderNode } from '../api/drive';
import { confirmDialog } from '../shell/dialogs';
import { Button } from '../components/arc/button/button';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';
import { AffilmasterDetailSectionHeader } from '../affilmaster/AffilmasterDetailFields';
import './klienti.css';

interface KlientDokumentyProps {
  load: () => Promise<DriveDocuments>;
  refresh: () => Promise<DriveDocuments>;
  saveFolder: (url: string | null) => Promise<unknown>;
  canManage: boolean;
}


const KIND: Array<{ test: RegExp; label: string; icon: 'doc' | 'sheet' | 'slides' | 'pdf' | 'image' | 'video' | 'archive' | 'file' }> = [
  { test: /google-apps\.document|msword|wordprocessingml|opendocument\.text/, label: 'Dokument', icon: 'doc' },
  { test: /google-apps\.spreadsheet|ms-excel|spreadsheetml|csv/, label: 'Tabulka', icon: 'sheet' },
  { test: /google-apps\.presentation|ms-powerpoint|presentationml/, label: 'Prezentace', icon: 'slides' },
  { test: /pdf/, label: 'PDF', icon: 'pdf' },
  { test: /^image\/|postscript|illustrator|photoshop/, label: 'Obrázek', icon: 'image' },
  { test: /^video\//, label: 'Video', icon: 'video' },
  { test: /zip|compressed|x-tar|x-7z|x-rar/, label: 'Archiv', icon: 'archive' },
];

function kindOf(mime: string) {
  return KIND.find((k) => k.test.test(mime)) ?? { label: 'Soubor', icon: 'file' as const };
}

function fmtSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 1 }).format(bytes / 1024 / 1024)} MB`;
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function pocetSouboru(n: number): string {
  if (n === 1) return '1 soubor';
  if (n >= 2 && n <= 4) return `${n} soubory`;
  return `${n} souborů`;
}

function Icon({ kind }: { kind: 'folder' | 'open' | 'copy' | 'edit' | 'refresh' | ReturnType<typeof kindOf>['icon'] }) {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (kind) {
    case 'folder':
      return <svg {...common}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>;
    case 'open':
      return <svg {...common}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>;
    case 'refresh':
      return <svg {...common}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>;
    case 'edit':
      return <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>;
    case 'copy':
      return <svg {...common}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>;
    case 'image':
      return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>;
    case 'video':
      return <svg {...common}><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>;
    case 'archive':
      return <svg {...common}><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>;
    case 'sheet':
      return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /></svg>;
    case 'slides':
      return <svg {...common}><rect x="2" y="4" width="20" height="13" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>;
    default:
      return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>;
  }
}

function FileRow({ file }: { file: DriveFile }) {
  const kind = kindOf(file.mimeType);
  const meta = [kind.label, fmtSize(file.size)].filter(Boolean).join(' · ');
  const date = fmtDate(file.modifiedAt);
  return (
    <li>
      <a className="klient-dokument" href={file.url} target="_blank" rel="noreferrer" title={`Otevřít ${file.name} na Google Drive`}>
        <span className={`klient-dokument-ico je-${kind.icon}`}><Icon kind={kind.icon} /></span>
        <span className="klient-dokument-name">{file.name}</span>
        <span className="klient-dokument-meta">{meta}</span>
        <span className="klient-dokument-date">{date ?? ''}</span>
        <span className="klient-dokument-open"><Icon kind="open" /></span>
      </a>
    </li>
  );
}

function countFiles(node: DriveFolderNode): number {
  return node.files.length + node.folders.reduce((n, f) => n + countFiles(f), 0);
}

interface Sekce {
  node: DriveFolderNode;
  title: string;
}

function rozdel(root: DriveFolderNode): { sekce: Sekce[]; ostatni: DriveFolderNode[] } {
  const sekce: Sekce[] = [];
  const ostatni: DriveFolderNode[] = [];
  const walk = (node: DriveFolderNode, path: string[]) => {
    for (const sub of node.folders) {
      if (sub.collapsed || countFiles(sub) === 0) {
        ostatni.push(sub);
        continue;
      }
      const title = [...path, sub.name];
      if (sub.files.length > 0) sekce.push({ node: sub, title: title.join(' / ') });
      walk(sub, title);
    }
  };
  walk(root, []);
  return { sekce, ostatni };
}

function FolderSection({ sekce }: { sekce: Sekce }) {
  const { node, title } = sekce;
  return (
    <section className="klient-dokumenty-slozka">
      <header className="klient-dokumenty-slozka-head">
        <a href={node.url} target="_blank" rel="noreferrer" title="Otevřít složku na Google Drive">
          <Icon kind="folder" />
          <span>{title}</span>
        </a>
        <small>{pocetSouboru(node.files.length)}</small>
      </header>
      <ArcPanel as="ul" className="klient-dokumenty-list">{node.files.map((f) => <FileRow key={f.id} file={f} />)}</ArcPanel>
    </section>
  );
}

function ShareHint({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(email).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <span className="klient-dokumenty-share">
      Složku na Drive nasdílejte jako <strong>Čtenář</strong> pro <code>{email}</code>
      <button type="button" onClick={copy} title="Zkopírovat adresu"><Icon kind="copy" /> {copied ? 'Zkopírováno' : 'Kopírovat'}</button>
    </span>
  );
}

export function KlientDokumenty({ load, refresh, saveFolder, canManage }: KlientDokumentyProps) {
  const [data, setData] = useState<DriveDocuments | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Načtení dokumentů selhalo'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  async function save(url: string | null) {
    setBusy(true); setFormErr(null);
    try {
      await saveFolder(url);
      setEditing(false);
      setData(null);
      setVersion((v) => v + 1);
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : 'Uložení složky selhalo');
    } finally {
      setBusy(false);
    }
  }

  async function reload() {
    setRefreshing(true);
    try {
      setData(await refresh());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Obnovení dokumentů selhalo');
    } finally {
      setRefreshing(false);
    }
  }

  if (error && !data) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!data) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;

  const tree = data.tree;
  const subtitle = tree
    ? `${pocetSouboru(countFiles(tree))} ve složce „${tree.name}"`
    : 'Nabídky, smlouvy a podklady klienta na Google Drive';
  const synced = fmtDateTime(data.syncedAt ?? null);

  return (
    <div className="klient-dokumenty">
      <AffilmasterDetailSectionHeader title="Dokumenty" description={`${subtitle}${synced ? ` · stav k ${synced}` : ''}`} action={<div className="klient-dokumenty-actions">
          {data.configured && data.folderUrl && !editing && (
            <Button type="button" variant="ghost" size="sm" className="klient-dokumenty-iconbtn" title="Načíst znovu z Google Drive" aria-label="Načíst znovu z Google Drive" disabled={refreshing} onClick={() => void reload()}>
              <Icon kind="refresh" />
            </Button>
          )}
          {canManage && !editing && data.folderUrl && (
            <Button type="button" variant="ghost" size="sm" className="klient-dokumenty-iconbtn" title="Změnit složku" aria-label="Změnit složku" onClick={() => { setDraft(data.folderUrl ?? ''); setFormErr(null); setEditing(true); }}>
              <Icon kind="edit" />
            </Button>
          )}
          {data.folderUrl && (
            <a className="btn btn-primary btn-sm klient-dokumenty-btn" href={data.folderUrl} target="_blank" rel="noreferrer"><Icon kind="open" /> Otevřít na Drive</a>
          )}
          {canManage && !editing && !data.folderUrl && (
            <Button type="button" size="sm" className="klient-dokumenty-btn" onClick={() => { setDraft(''); setFormErr(null); setEditing(true); }}>
              <Icon kind="folder" /> Nastavit složku
            </Button>
          )}
        </div>} />

      {editing && (
        <ArcPanel as="form" className="klient-dokumenty-form" onSubmit={(e) => { e.preventDefault(); void save(draft.trim() || null); }}>
          <label htmlFor="klient-dokumenty-url">Odkaz na složku klienta na Google Drive</label>
          <div className="klient-dokumenty-form-row">
            <input id="klient-dokumenty-url" className="form-control form-control-sm" autoFocus value={draft} disabled={busy} placeholder="https://drive.google.com/drive/folders/…" onChange={(e) => setDraft(e.target.value)} />
            <Button type="submit" size="sm" disabled={busy}>{busy ? 'Ukládám…' : 'Uložit'}</Button>
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => setEditing(false)}>Zrušit</Button>
            {data.folderUrl && <Button type="button" variant="danger" size="sm" disabled={busy} onClick={() => { void confirmDialog({ title: 'Odebrat složku z karty klienta?', message: 'Na Google Drive se nic nesmaže, karta jen přestane složku zobrazovat.', confirmLabel: 'Odebrat', danger: true }).then((ok) => { if (ok) void save(null); }); }}>Odebrat složku</Button>}
          </div>
          {formErr && <div className="text-danger small">{formErr}</div>}
          {data.shareEmail && <ShareHint email={data.shareEmail} />}
        </ArcPanel>
      )}

      {!data.configured && (
        <div className="klient-reporting-empty" role="status">
          <div>
            <strong>Čtení z Google Drive zatím není připojené</strong>
            <p>Pro tento Space ještě není nastavený účet, pod kterým Platform 1 složky čte. Odkaz na složku lze uložit už teď, seznam souborů se objeví po připojení.</p>
          </div>
        </div>
      )}

      {data.configured && !data.folderUrl && !editing && (
        <div className="prototype-empty-contact">
          Klient zatím nemá nastavenou složku na Drive.
          {canManage && data.shareEmail && <div className="mt-2"><ShareHint email={data.shareEmail} /></div>}
        </div>
      )}

      {data.error && (
        <div className="klient-reporting-empty" role="alert">
          <div>
            <strong>{data.error.code === 'NOT_SHARED' ? 'Složku se nepodařilo přečíst' : 'Dokumenty se nepodařilo načíst'}</strong>
            <p>{data.error.message}</p>
            {data.error.code === 'NOT_SHARED' && data.shareEmail && <p><ShareHint email={data.shareEmail} /></p>}
          </div>
        </div>
      )}

      {tree && (() => {
        const { sekce, ostatni } = rozdel(tree);
        return (
          <>
            {tree.files.length > 0 && (
              <section className="klient-dokumenty-slozka je-koren">
                <ArcPanel as="ul" className="klient-dokumenty-list">{tree.files.map((f) => <FileRow key={f.id} file={f} />)}</ArcPanel>
              </section>
            )}
            {sekce.map((s) => <FolderSection key={s.node.id} sekce={s} />)}
            {tree.files.length === 0 && sekce.length === 0 && <p className="klient-dokumenty-prazdna">Ve složce zatím nejsou žádné dokumenty.</p>}
            {ostatni.length > 0 && (
              <div className="klient-dokumenty-ostatni">
                <span>Další složky na Drive</span>
                {ostatni.map((f) => (
                  <a key={f.id} href={f.url} target="_blank" rel="noreferrer" title={`Otevřít složku ${f.name} na Google Drive`}><Icon kind="folder" />{f.name}</a>
                ))}
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}

